import React from "react";
import {
  UserCircle, CalendarCheck, CalendarOff, Wallet, FolderOpen, Inbox, Package, Plus,
} from "lucide-react";
import { useDashTheme } from "../../hooks/useDashTheme";
import { PageShell } from "../../components/PageShell";
import { SectionCard, Button, Badge, Avatar } from "../../components/primitives";
import { DataTable, EmptyState } from "../../components/DataTable";
import { Drawer } from "../../components/Drawer";
import { Input, Field, Select, Textarea } from "../../components/forms";
import { Grid, Stack, Row, KVGrid, Meter, Hint, StatusBadge, Timeline } from "./kit";
import { useHrms, useFetch, fmtDate, fmtDateShort, fmtMoney, fullName, monthKey } from "./useHrms";

// HRMS ▸ My HR — employee self-service, fully live. Everything scopes to the
// signed-in user server-side; this page never passes someone else's id.

const TABS = [
  { key: "profile", label: "Profile", icon: <UserCircle size={14} /> },
  { key: "attendance", label: "Attendance", icon: <CalendarCheck size={14} /> },
  { key: "leave", label: "Leave", icon: <CalendarOff size={14} /> },
  { key: "payslips", label: "Payslips", icon: <Wallet size={14} /> },
  { key: "documents", label: "Documents", icon: <FolderOpen size={14} /> },
  { key: "requests", label: "Requests", icon: <Inbox size={14} /> },
  { key: "assets", label: "Assets & tickets", icon: <Package size={14} /> },
];

export default function HrmsMe() {
  const t = useDashTheme();
  const { user } = useHrms();
  const [tab, setTab] = React.useState("profile");

  return (
    <PageShell
      eyebrow="HRMS"
      title="My HR"
      subtitle="Your profile, time, leave, pay and requests — self-service"
      icon={<UserCircle size={16} />}
      tabs={TABS}
      activeTab={tab}
      onTabChange={setTab}
    >
      {tab === "profile" && <ProfileTab t={t} userId={user?.id || user?._id} />}
      {tab === "attendance" && <MyAttendance t={t} />}
      {tab === "leave" && <MyLeave t={t} />}
      {tab === "payslips" && <MyPayslips t={t} />}
      {tab === "documents" && <MyDocuments t={t} />}
      {tab === "requests" && <MyRequests t={t} />}
      {tab === "assets" && <MyAssets t={t} />}
    </PageShell>
  );
}

function ProfileTab({ t, userId }) {
  const { patch } = useHrms();
  const { data, loading, refresh } = useFetch(userId ? `/employees/${userId}` : null, [userId]);
  const [edit, setEdit] = React.useState(false);
  const [form, setForm] = React.useState({});
  const [notice, setNotice] = React.useState(null);

  if (!userId || loading) return <EmptyState title="Loading profile…" />;
  const u = data?.user || {};
  const p = data?.profile || {};

  const save = async () => {
    const res = await patch(`/employees/${userId}`, form);
    setEdit(false);
    setForm({});
    setNotice(res.changeRequest
      ? "Saved. Sensitive fields went to HR as a change request."
      : "Saved.");
    refresh();
  };

  return (
    <Stack gap={t.s[4]}>
      <SectionCard
        title={fullName(u)} subtitle={`${u.position || "—"} · ${u.email}`}
        icon={<UserCircle size={13} />} padding={16}
        actions={<Button size="sm" variant="secondary" onClick={() => setEdit(true)}>Edit</Button>}
      >
        <Row gap={16} align="flex-start" wrap>
          <Avatar name={fullName(u)} src={u.avatar} size={56} />
          <div style={{ flex: 1, minWidth: 260 }}>
            <KVGrid items={[
              { label: "Employee ID", value: u.employeeId, mono: true },
              { label: "Department", value: u.department },
              { label: "Reporting manager", value: fullName(u.reportingManager) },
              { label: "Joined", value: fmtDate(p.joinedAt) },
              { label: "Employment type", value: (p.employmentType || "").replace("_", " ") },
              { label: "Status", value: <StatusBadge value={cap(p.status || "active")} /> },
            ]} />
          </div>
        </Row>
      </SectionCard>

      {notice && <Hint>{notice}</Hint>}

      <Grid min={320}>
        <SectionCard title="Personal" padding={16}>
          <KVGrid items={[
            { label: "Phone", value: p.phone },
            { label: "Date of birth", value: fmtDate(p.dob) },
            { label: "Blood group", value: p.bloodGroup },
            { label: "Address", value: p.address },
            { label: "Emergency contact", value: p.emergencyContact?.name ? `${p.emergencyContact.name} · ${p.emergencyContact.phone || ""}` : null },
          ]} />
        </SectionCard>
        <SectionCard title="Statutory & bank" subtitle="Changes need HR approval" padding={16}>
          <KVGrid items={[
            { label: "PAN", value: p.pan, mono: true, locked: true },
            { label: "UAN", value: p.uan, mono: true, locked: true },
            { label: "PF number", value: p.pfNumber, mono: true, locked: true },
            { label: "Bank", value: p.bank?.name ? `${p.bank.name} · ${p.bank.accountNumberMasked || ""}` : null, locked: true },
          ]} />
        </SectionCard>
      </Grid>

      {(data?.auditTrail || []).length > 0 && (
        <SectionCard title="Change history" padding={16}>
          <Timeline items={data.auditTrail.slice(0, 8).map((a) => ({
            what: a.what,
            detail: a.field ? `${a.fromValue ?? "—"} → ${a.toValue ?? "—"}` : "",
            when: fmtDate(a.createdAt),
            by: a.changedBy ? fullName(a.changedBy) : "System",
          }))} />
        </SectionCard>
      )}

      <Drawer open={edit} onClose={() => setEdit(false)} title="Edit my profile" subtitle="Phone/address apply instantly. Statutory fields go to HR for approval." width={440}>
        <Stack gap={t.s[4]}>
          <Field label="Phone"><Input defaultValue={p.phone || ""} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} /></Field>
          <Field label="Address"><Textarea defaultValue={p.address || ""} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} /></Field>
          <Field label="Blood group"><Input defaultValue={p.bloodGroup || ""} onChange={(e) => setForm((f) => ({ ...f, bloodGroup: e.target.value }))} /></Field>
          <Field label="PAN (needs approval)"><Input defaultValue={p.pan || ""} onChange={(e) => setForm((f) => ({ ...f, pan: e.target.value }))} /></Field>
          <Field label="UAN (needs approval)"><Input defaultValue={p.uan || ""} onChange={(e) => setForm((f) => ({ ...f, uan: e.target.value }))} /></Field>
          <Row gap={8}>
            <Button variant="primary" onClick={save}>Save</Button>
            <Button variant="ghost" onClick={() => setEdit(false)}>Cancel</Button>
          </Row>
        </Stack>
      </Drawer>
    </Stack>
  );
}

function MyAttendance({ t }) {
  const { post } = useHrms();
  const month = monthKey();
  const from = `${month}-01`;
  const to = new Date().toISOString().slice(0, 10);
  const { data, loading, refresh } = useFetch(`/attendance/days?from=${from}&to=${to}`);
  const [correcting, setCorrecting] = React.useState(null);
  const [changeText, setChangeText] = React.useState("");
  const [reason, setReason] = React.useState("");

  const rows = data || [];
  const submit = async () => {
    await post("/attendance/corrections", {
      date: correcting.date, requestedChange: changeText || "Regularise this day", reason,
    });
    setCorrecting(null); setChangeText(""); setReason("");
    refresh();
  };

  return (
    <Stack gap={t.s[4]}>
      <Hint>Days come from your tracked activity, approved leave and holidays. Raise a correction on any day that looks wrong.</Hint>
      <SectionCard title="This month" padding={0}>
        <DataTable
          loading={loading}
          columns={[
            { key: "date", header: "Date", render: (r) => fmtDate(r.date) },
            { key: "status", header: "Status", render: (r) => <StatusBadge value={cap(r.status)} /> },
            { key: "worked", header: "Hours", align: "right", render: (r) => r.workedMinutes ? `${Math.floor(r.workedMinutes / 60)}h ${r.workedMinutes % 60}m` : "—" },
            { key: "flag", header: "Note", render: (r) => r.flag || "" },
            {
              key: "act", header: "", align: "right",
              render: (r) => ["absent", "half_day", "missing_punch"].includes(r.status)
                ? <Button size="sm" variant="ghost" onClick={() => setCorrecting(r)}>Fix</Button> : null,
            },
          ]}
          rows={rows}
          emptyState={<EmptyState title="No attendance yet" description="Rows appear once HR derives attendance for this month." />}
        />
      </SectionCard>

      <Drawer open={!!correcting} onClose={() => setCorrecting(null)} title={`Correction · ${correcting ? fmtDate(correcting.date) : ""}`} width={420}>
        <Stack gap={t.s[4]}>
          <Field label="What should this day show?">
            <Input value={changeText} onChange={(e) => setChangeText(e.target.value)} placeholder="e.g. Worked on client site, full day" />
          </Field>
          <Field label="Reason"><Textarea value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
          <Row gap={8}>
            <Button variant="primary" onClick={submit}>Send to manager</Button>
            <Button variant="ghost" onClick={() => setCorrecting(null)}>Cancel</Button>
          </Row>
        </Stack>
      </Drawer>
    </Stack>
  );
}

function MyLeave({ t }) {
  const { post } = useHrms();
  const balances = useFetch("/leave/balances");
  const requests = useFetch("/leave/requests");
  const [applying, setApplying] = React.useState(false);
  const [form, setForm] = React.useState({ leaveTypeId: "", fromDate: "", toDate: "", reason: "" });
  const [error, setError] = React.useState(null);

  const apply = async () => {
    try {
      setError(null);
      await post("/leave/requests", form);
      setApplying(false);
      setForm({ leaveTypeId: "", fromDate: "", toDate: "", reason: "" });
      balances.refresh(); requests.refresh();
    } catch (e) {
      setError(e.message);
    }
  };

  const cancel = async (id) => {
    await post(`/leave/requests/${id}/cancel`, {});
    balances.refresh(); requests.refresh();
  };

  return (
    <Stack gap={t.s[4]}>
      <Grid min={200}>
        {(balances.data || []).map((b) => (
          <SectionCard key={b._id} title={b.leaveTypeId?.name} padding={16}>
            <Row justify="space-between" style={{ marginBottom: 8 }}>
              <span style={{ fontSize: 22, fontWeight: 600, color: t.c.text }}>{b.available}</span>
              <span style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>of {b.opening + b.accrued}</span>
            </Row>
            <Meter value={b.available} max={b.opening + b.accrued || 1} tone={b.available <= 1 ? "warning" : "brand"} />
            {b.pending > 0 && <div style={{ marginTop: 6, fontSize: t.font.size.xs, color: t.c.textTertiary }}>{b.pending} pending approval</div>}
          </SectionCard>
        ))}
      </Grid>

      <SectionCard
        title="My requests" padding={0}
        actions={<Button size="sm" variant="primary" leftIcon={<Plus size={13} />} onClick={() => setApplying(true)}>Apply</Button>}
      >
        <DataTable
          loading={requests.loading}
          columns={[
            { key: "type", header: "Type", render: (r) => r.leaveTypeId?.name },
            { key: "dates", header: "Dates", render: (r) => `${fmtDateShort(r.fromDate)} → ${fmtDateShort(r.toDate)}` },
            { key: "days", header: "Days", align: "right", render: (r) => r.days },
            { key: "status", header: "Status", render: (r) => <StatusBadge value={cap(r.status)} /> },
            {
              key: "act", header: "", align: "right",
              render: (r) => r.status === "pending"
                ? <Button size="sm" variant="ghost" onClick={() => cancel(r._id)}>Cancel</Button> : null,
            },
          ]}
          rows={requests.data || []}
          emptyState={<EmptyState title="No leave requests" description="Apply for leave and track it here." />}
        />
      </SectionCard>

      <Drawer open={applying} onClose={() => setApplying(false)} title="Apply for leave" width={420}>
        <Stack gap={t.s[4]}>
          <Field label="Leave type">
            <Select value={form.leaveTypeId} onChange={(e) => setForm((f) => ({ ...f, leaveTypeId: e.target.value }))}>
              <option value="">Select…</option>
              {(balances.data || []).map((b) => (
                <option key={b.leaveTypeId?._id} value={b.leaveTypeId?._id}>{b.leaveTypeId?.name} ({b.available} left)</option>
              ))}
            </Select>
          </Field>
          <Row gap={12}>
            <Field label="From"><Input type="date" value={form.fromDate} onChange={(e) => setForm((f) => ({ ...f, fromDate: e.target.value }))} /></Field>
            <Field label="To"><Input type="date" value={form.toDate} onChange={(e) => setForm((f) => ({ ...f, toDate: e.target.value }))} /></Field>
          </Row>
          <Field label="Reason"><Textarea value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} /></Field>
          {error && <Hint>{error}</Hint>}
          <Row gap={8}>
            <Button variant="primary" onClick={apply} disabled={!form.leaveTypeId || !form.fromDate || !form.toDate}>Submit</Button>
            <Button variant="ghost" onClick={() => setApplying(false)}>Cancel</Button>
          </Row>
        </Stack>
      </Drawer>
    </Stack>
  );
}

function MyPayslips({ t }) {
  const { data, loading } = useFetch("/payroll/payslips");
  return (
    <SectionCard title="Payslips" subtitle="Visible once payroll publishes a cycle" padding={0}>
      <DataTable
        loading={loading}
        columns={[
          { key: "month", header: "Month" },
          { key: "gross", header: "Gross", align: "right", render: (r) => fmtMoney(r.gross) },
          { key: "deductions", header: "Deductions", align: "right", render: (r) => fmtMoney(r.deductions) },
          { key: "net", header: "Net pay", align: "right", render: (r) => <strong>{fmtMoney(r.net)}</strong> },
          { key: "lop", header: "LOP days", align: "right", render: (r) => r.lopDays || 0 },
          { key: "status", header: "Status", render: (r) => <StatusBadge value={cap(r.status)} /> },
        ]}
        rows={data || []}
        emptyState={<EmptyState title="No payslips yet" description="Published payslips appear here." />}
      />
    </SectionCard>
  );
}

function MyDocuments({ t }) {
  const { postForm } = useHrms();
  const { data, loading, refresh } = useFetch("/documents");
  const [adding, setAdding] = React.useState(false);
  const [name, setName] = React.useState("");
  const [folder, setFolder] = React.useState("General");
  const [file, setFile] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await postForm("/documents", { name: name.trim(), folder }, file);
      setAdding(false); setName(""); setFile(null);
      refresh();
    } finally { setBusy(false); }
  };

  return (
    <Stack gap={t.s[4]}>
      <SectionCard
        title="My documents" padding={0}
        actions={<Button size="sm" variant="secondary" leftIcon={<Plus size={13} />} onClick={() => setAdding(true)}>Add</Button>}
      >
        <DataTable
          loading={loading}
          columns={[
            { key: "name", header: "Document" },
            { key: "folder", header: "Folder" },
            { key: "status", header: "Status", render: (r) => <StatusBadge value={cap(r.status)} /> },
            { key: "uploadedAt", header: "Uploaded", render: (r) => fmtDate(r.uploadedAt) },
            {
              key: "file", header: "", align: "right",
              render: (r) => r.fileUrl
                ? <a href={r.fileUrl} target="_blank" rel="noreferrer" style={{ fontSize: t.font.size.sm, color: t.c.primary }}>View</a>
                : null,
            },
          ]}
          rows={data || []}
          emptyState={<EmptyState title="No documents" description="Add identity, education and other records here." />}
        />
      </SectionCard>

      <Drawer open={adding} onClose={() => setAdding(false)} title="Add document" width={400}>
        <Stack gap={t.s[4]}>
          <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="PAN card" /></Field>
          <Field label="Folder">
            <Select value={folder} onChange={(e) => setFolder(e.target.value)}>
              {["General", "Identity", "Education", "Employment", "Financial"].map((f) => <option key={f}>{f}</option>)}
            </Select>
          </Field>
          <Field label="File" hint="PDF or image, up to 10 MB">
            <Input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </Field>
          <Row gap={8}>
            <Button variant="primary" onClick={add} disabled={busy}>{busy ? "Uploading…" : "Save"}</Button>
            <Button variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
          </Row>
        </Stack>
      </Drawer>
    </Stack>
  );
}

function MyRequests({ t }) {
  const { postForm } = useHrms();
  const { data, loading, refresh } = useFetch("/approvals/mine");
  const [claiming, setClaiming] = React.useState(false);
  const [form, setForm] = React.useState({ head: "", amount: "" });
  const [bill, setBill] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const claim = async () => {
    if (!form.head.trim() || !form.amount) return;
    setBusy(true);
    try {
      await postForm("/payroll/reimbursements", form, bill);
      setClaiming(false);
      setForm({ head: "", amount: "" }); setBill(null);
      refresh();
    } finally { setBusy(false); }
  };

  return (
    <Stack gap={t.s[4]}>
      <SectionCard
        title="My requests" subtitle="Everything you have sent for approval" padding={0}
        actions={<Button size="sm" variant="secondary" leftIcon={<Plus size={13} />} onClick={() => setClaiming(true)}>Claim reimbursement</Button>}
      >
        <DataTable
          loading={loading}
          columns={[
            { key: "type", header: "Type", render: (r) => cap(String(r.type).replace("_", " ")) },
            { key: "summary", header: "Request", render: (r) => r.summary || "—" },
            {
              key: "with", header: "Waiting on",
              render: (r) => r.status === "pending" ? fullName(r.chain?.[r.currentStage]?.approverId) : "—",
            },
            { key: "status", header: "Status", render: (r) => <StatusBadge value={cap(r.status)} /> },
            { key: "createdAt", header: "Raised", render: (r) => fmtDate(r.createdAt) },
          ]}
          rows={data || []}
          emptyState={<EmptyState title="Nothing pending" description="Leave, corrections and profile changes appear here." />}
        />
      </SectionCard>

      <Drawer open={claiming} onClose={() => setClaiming(false)} title="Claim reimbursement" width={420}>
        <Stack gap={t.s[4]}>
          <Field label="Head"><Input value={form.head} onChange={(e) => setForm((f) => ({ ...f, head: e.target.value }))} placeholder="Client travel" /></Field>
          <Field label="Amount (₹)"><Input type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} /></Field>
          <Field label="Bill" hint="Attach the receipt — claims without a bill are usually rejected">
            <Input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={(e) => setBill(e.target.files?.[0] || null)} />
          </Field>
          <Row gap={8}>
            <Button variant="primary" onClick={claim} disabled={busy}>{busy ? "Submitting…" : "Submit claim"}</Button>
            <Button variant="ghost" onClick={() => setClaiming(false)}>Cancel</Button>
          </Row>
        </Stack>
      </Drawer>
    </Stack>
  );
}

function MyAssets({ t }) {
  const { post } = useHrms();
  const assets = useFetch("/assets");
  const tickets = useFetch("/tickets");
  const [raising, setRaising] = React.useState(false);
  const [form, setForm] = React.useState({ subject: "", detail: "", category: "it", priority: "medium" });

  const raise = async () => {
    if (!form.subject.trim()) return;
    await post("/tickets", form);
    setRaising(false);
    setForm({ subject: "", detail: "", category: "it", priority: "medium" });
    tickets.refresh();
  };

  return (
    <Stack gap={t.s[4]}>
      <SectionCard title="Assets with me" padding={0}>
        <DataTable
          loading={assets.loading}
          columns={[
            { key: "tag", header: "Tag" },
            { key: "item", header: "Item" },
            { key: "category", header: "Category", render: (r) => cap(r.category) },
            { key: "since", header: "Since", render: (r) => fmtDate(r.assignedSince) },
            { key: "status", header: "Status", render: (r) => <StatusBadge value={cap(r.status)} /> },
          ]}
          rows={assets.data || []}
          emptyState={<EmptyState title="No assets assigned" />}
        />
      </SectionCard>

      <SectionCard
        title="My tickets" padding={0}
        actions={<Button size="sm" variant="primary" leftIcon={<Plus size={13} />} onClick={() => setRaising(true)}>Raise ticket</Button>}
      >
        <DataTable
          loading={tickets.loading}
          columns={[
            { key: "subject", header: "Subject" },
            { key: "category", header: "Category", render: (r) => cap(r.category) },
            { key: "priority", header: "Priority", render: (r) => <Badge tone={r.priority === "high" ? "negative" : r.priority === "medium" ? "warning" : "neutral"}>{r.priority}</Badge> },
            { key: "status", header: "Status", render: (r) => <StatusBadge value={cap(String(r.status).replace("_", " "))} /> },
            { key: "createdAt", header: "Raised", render: (r) => fmtDate(r.createdAt) },
          ]}
          rows={tickets.data || []}
          emptyState={<EmptyState title="No tickets" description="IT, HR and admin requests go here." />}
        />
      </SectionCard>

      <Drawer open={raising} onClose={() => setRaising(false)} title="Raise a ticket" width={420}>
        <Stack gap={t.s[4]}>
          <Field label="Subject"><Input value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} /></Field>
          <Row gap={12}>
            <Field label="Category">
              <Select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                {["it", "hr", "admin", "payroll", "other"].map((c) => <option key={c} value={c}>{cap(c)}</option>)}
              </Select>
            </Field>
            <Field label="Priority">
              <Select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}>
                {["low", "medium", "high"].map((p) => <option key={p} value={p}>{cap(p)}</option>)}
              </Select>
            </Field>
          </Row>
          <Field label="Details"><Textarea value={form.detail} onChange={(e) => setForm((f) => ({ ...f, detail: e.target.value }))} /></Field>
          <Row gap={8}>
            <Button variant="primary" onClick={raise}>Submit</Button>
            <Button variant="ghost" onClick={() => setRaising(false)}>Cancel</Button>
          </Row>
        </Stack>
      </Drawer>
    </Stack>
  );
}

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ") : s);
